/**
 * Change log for Risk settings and gate strategies (dbo.RiskSettingChangeAudit):
 * who changed what, when, from what to what, and why.
 */

import { useCallback, useEffect, useState } from 'react';

import { loadRiskHistory, type RiskSettingChange, type RiskSettingRow } from '@/services/riskSettingsApi';

import { displayValue } from './controls';

interface Props {
  readonly rows: ReadonlyArray<RiskSettingRow>;
  /** Bumped by the parent after every save so the log refreshes. */
  readonly version: number;
}

export function ChangeLog({ rows, version }: Props): JSX.Element {
  const [filter, setFilter] = useState('');
  const [changes, setChanges] = useState<ReadonlyArray<RiskSettingChange>>([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setBusy(true); setErr(null);
    try {
      setChanges(await loadRiskHistory(filter || undefined, 200));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not load the change log.');
    } finally {
      setBusy(false);
    }
  }, [filter]);

  useEffect(() => { void refresh(); }, [refresh, version]);

  const rowFor = (key: string): RiskSettingRow | undefined => rows.find((r) => r.settingKey === key);
  const show = (c: RiskSettingChange, v: string | null): string => {
    const r = c.kind === 'setting' ? rowFor(c.targetKey) : undefined;
    if (v == null) return c.kind === 'setting' ? '(default)' : '(none)';
    return r ? displayValue(r, v) : v;
  };
  const what = (c: RiskSettingChange): string =>
    c.kind === 'gate' ? `Gate ${c.targetKey.replace('/', ' — ')}` : (rowFor(c.targetKey)?.label ?? c.targetKey);

  return (
    <section className="card">
      <div className="card-header">
        <h2>Change log</h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <select className="rs-select" style={{ width: 280 }} value={filter} aria-label="Show changes for"
            onChange={(e) => setFilter(e.target.value)}>
            <option value="">All settings and gates</option>
            {rows.map((r) => <option key={r.settingKey} value={r.settingKey}>{r.label ?? r.settingKey}</option>)}
          </select>
          <button type="button" className="btn btn--ghost btn--sm" disabled={busy} onClick={() => void refresh()}>Refresh</button>
        </div>
      </div>
      {err && <p className="banner banner--err" role="alert">{err}</p>}
      {!err && !busy && changes.length === 0 && <p className="empty">No changes recorded yet.</p>}
      {changes.length > 0 && (
        <table className="table rs-log">
          <thead>
            <tr>
              <th scope="col">When</th>
              <th scope="col">Who</th>
              <th scope="col">Setting</th>
              <th scope="col">Change</th>
              <th scope="col">Reason</th>
            </tr>
          </thead>
          <tbody>
            {changes.map((c) => (
              <tr key={c.id}>
                <td style={{ whiteSpace: 'nowrap' }}>{new Date(c.changedAt).toLocaleString()}</td>
                <td>{c.changedBy}</td>
                <td>{what(c)}</td>
                <td>
                  <span className="rs-confirm__old">{show(c, c.oldValue)}</span>
                  {' → '}
                  <span className="rs-confirm__new">{show(c, c.newValue)}</span>
                </td>
                <td>{c.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
